import {
	centroid,
	normalizePoint,
	pointInPolygon,
	seedShapePoints,
} from 'features/Responder/features/Zones';

const toRadians = (value) => (value * Math.PI) / 180;

const normalizePolylinePoint = (pt) => {
	if (!pt) return null;
	if (Array.isArray(pt) && pt.length >= 2) {
		return { lat: Number(pt[0]), lng: Number(pt[1]) };
	}
	if (typeof pt === 'object') {
		const lat = Number(pt.lat ?? pt.latitude);
		const lng = Number(pt.lng ?? pt.longitude);
		return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
	}
	return null;
};

/**
 * Resolve the best available center for a zone checkpoint.
 * @param {object} checkpoint
 * @returns {{lat: number, lng: number}|null}
 */
export const resolveZoneCenter = (checkpoint) => {
	const baseLat = Number(checkpoint?.lat);
	const baseLng = Number(checkpoint?.lng);
	if (Number.isFinite(baseLat) && Number.isFinite(baseLng)) {
		return { lat: baseLat, lng: baseLng };
	}
	const geometry = checkpoint?.geometry || {};
	const raw = geometry.points ?? geometry.coord ?? geometry.polygon ?? [];
	const points = Array.isArray(raw) ? raw.map((pt) => normalizePoint(pt)).filter(Boolean) : [];
	const center = centroid(points) || points[0];
	return center ? { lat: center.lat, lng: center.lng } : null;
};

/**
 * Resolve zone polygon points, seeding a circle if needed.
 * @param {object} checkpoint
 * @returns {Array<{lat: number, lng: number}>}
 */
export const resolveZonePolygon = (checkpoint) => {
	const geometry = checkpoint?.geometry || {};
	const raw = geometry.points ?? geometry.coord ?? geometry.polygon ?? [];
	const points = Array.isArray(raw) ? raw.map((pt) => normalizePoint(pt)).filter(Boolean) : [];
	if (points.length >= 3) return points;
	const type = (geometry?.type || geometry?.kind || '').toString().toLowerCase();
	if (type === 'circle' || geometry?.isZone) {
		const center = resolveZoneCenter(checkpoint);
		const radius = Number(
			geometry?.radiusMeters ??
				checkpoint?.radiusMeters ??
				geometry?.radius ??
				checkpoint?.radius
		);
		if (center && Number.isFinite(radius) && radius > 0) {
			return seedShapePoints(center, 'circle', radius);
		}
	}
	return points;
};

/**
 * Haversine distance between two lat/lng points.
 * @param {{lat: number, lng: number}} a
 * @param {{lat: number, lng: number}} b
 * @returns {number}
 */
export const distanceMeters = (a, b) => {
	if (!a || !b) return Number.POSITIVE_INFINITY;
	const lat1 = toRadians(a.lat);
	const lat2 = toRadians(b.lat);
	const dLat = lat2 - lat1;
	const dLng = toRadians(b.lng - a.lng);
	const sinLat = Math.sin(dLat / 2);
	const sinLng = Math.sin(dLng / 2);
	const hav =
		sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
	return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(hav)));
};

/**
 * Project a point to the nearest polygon edge.
 * @param {{lat: number, lng: number}} point
 * @param {Array<{lat: number, lng: number}>} polygon
 * @returns {{lat: number, lng: number}|null}
 */
export const nearestPointOnPolygon = (point, polygon) => {
	if (!point || !Array.isArray(polygon) || polygon.length < 2) return null;
	const scale = Math.cos((point.lat * Math.PI) / 180) || 1;
	const px = point.lng * scale;
	const py = point.lat;
	let best = null;
	for (let i = 0; i < polygon.length; i += 1) {
		const a = polygon[i];
		const b = polygon[(i + 1) % polygon.length];
		if (!a || !b) continue;
		const ax = a.lng * scale;
		const ay = a.lat;
		const bx = b.lng * scale;
		const by = b.lat;
		const dx = bx - ax;
		const dy = by - ay;
		const denom = dx * dx + dy * dy || 1;
		const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / denom));
		const projX = ax + dx * t;
		const projY = ay + dy * t;
		const distSq = (px - projX) * (px - projX) + (py - projY) * (py - projY);
		if (!best || distSq < best.distSq) {
			best = { distSq, lat: projY, lng: projX / scale };
		}
	}
	return best ? { lat: best.lat, lng: best.lng } : null;
};

/**
 * Clamp a lat/lng to the polygon boundary if outside.
 * @param {{lat: number, lng: number}} latlng
 * @param {Array<{lat: number, lng: number}>} polygon
 * @returns {{lat: number, lng: number}}
 */
export const clampLatLngToPolygon = (latlng, polygon) => {
	if (!latlng || !Array.isArray(polygon) || polygon.length < 3) return latlng;
	if (pointInPolygon(latlng, polygon)) return latlng;
	return nearestPointOnPolygon(latlng, polygon) || latlng;
};

/**
 * Sample interior points along a segment for testing entry paths.
 * @param {{lat: number, lng: number}} start
 * @param {{lat: number, lng: number}} end
 * @param {number} count
 * @returns {Array<{lat: number, lng: number}>}
 */
export const sampleInteriorPoints = (start, end, count = 4) => {
	if (!start || !end || count <= 0) return [];
	const points = [];
	for (let i = 1; i <= count; i += 1) {
		const t = i / (count + 1);
		points.push({
			lat: start.lat + (end.lat - start.lat) * t,
			lng: start.lng + (end.lng - start.lng) * t,
		});
	}
	return points;
};

const EDGE_TOLERANCE_METERS = 3;

/**
 * Treat near-edge snapped points as inside to cover boundary roads.
 * @param {{lat: number, lng: number}} point
 * @param {Array<{lat: number, lng: number}>} polygon
 * @returns {boolean}
 */
export const isPointInsideOrOnZone = (point, polygon) => {
	if (!point || !Array.isArray(polygon) || polygon.length < 3) return false;
	if (pointInPolygon(point, polygon)) return true;
	const nearest = nearestPointOnPolygon(point, polygon);
	if (!nearest) return false;
	return distanceMeters(point, nearest) <= EDGE_TOLERANCE_METERS;
};

/**
 * Find the polygon edge intersection along a ray from "from" to "toward".
 * @param {{lat: number, lng: number}} from
 * @param {{lat: number, lng: number}} toward
 * @param {Array<{lat: number, lng: number}>} polygon
 * @returns {{lat: number, lng: number}|null}
 */
export const findEdgeIntersection = (from, toward, polygon) => {
	if (!from || !toward || !Array.isArray(polygon) || polygon.length < 2) return null;
	const dx = toward.lng - from.lng;
	const dy = toward.lat - from.lat;
	if (!Number.isFinite(dx) || !Number.isFinite(dy) || (dx === 0 && dy === 0)) return null;
	const scale = Math.cos((from.lat * Math.PI) / 180) || 1;
	const p = { x: from.lng * scale, y: from.lat };
	const r = { x: dx * scale, y: dy };
	let bestT = Infinity;
	let bestPoint = null;
	for (let i = 0; i < polygon.length; i += 1) {
		const a = polygon[i];
		const b = polygon[(i + 1) % polygon.length];
		if (!a || !b) continue;
		const q = { x: a.lng * scale, y: a.lat };
		const s = { x: (b.lng - a.lng) * scale, y: b.lat - a.lat };
		const denom = r.x * s.y - r.y * s.x;
		if (Math.abs(denom) < 1e-12) continue;
		const qp = { x: q.x - p.x, y: q.y - p.y };
		const t = (qp.x * s.y - qp.y * s.x) / denom;
		const u = (qp.x * r.y - qp.y * r.x) / denom;
		if (t >= 0 && u >= 0 && u <= 1 && t < bestT) {
			bestT = t;
			bestPoint = {
				lat: p.y + t * r.y,
				lng: (p.x + t * r.x) / scale,
			};
		}
	}
	return bestPoint;
};

const segmentPolygonIntersection = (start, end, polygon) => {
	if (!start || !end || !Array.isArray(polygon) || polygon.length < 2) return null;
	const scale = Math.cos(((start.lat + end.lat) / 2) * (Math.PI / 180)) || 1;
	const p = { x: start.lng * scale, y: start.lat };
	const r = { x: (end.lng - start.lng) * scale, y: end.lat - start.lat };
	let bestT = Infinity;
	let bestPoint = null;
	for (let i = 0; i < polygon.length; i += 1) {
		const a = polygon[i];
		const b = polygon[(i + 1) % polygon.length];
		if (!a || !b) continue;
		const q = { x: a.lng * scale, y: a.lat };
		const s = { x: (b.lng - a.lng) * scale, y: b.lat - a.lat };
		const denom = r.x * s.y - r.y * s.x;
		if (Math.abs(denom) < 1e-12) continue;
		const qp = { x: q.x - p.x, y: q.y - p.y };
		const t = (qp.x * s.y - qp.y * s.x) / denom;
		const u = (qp.x * r.y - qp.y * r.x) / denom;
		if (t >= 0 && t <= 1 && u >= 0 && u <= 1 && t < bestT) {
			bestT = t;
			bestPoint = {
				lat: p.y + t * r.y,
				lng: (p.x + t * r.x) / scale,
			};
		}
	}
	return bestPoint;
};

/**
 * Determine if a polyline intersects or passes through a polygon.
 * @param {Array<{lat: number, lng: number}>|Array<[number, number]>} polyline
 * @param {Array<{lat: number, lng: number}>} polygon
 * @returns {boolean}
 */
export const polylineIntersectsPolygon = (polyline, polygon) => {
	if (!Array.isArray(polyline) || polyline.length < 2) return false;
	if (!Array.isArray(polygon) || polygon.length < 3) return false;
	const points = polyline.map((pt) => normalizePolylinePoint(pt)).filter(Boolean);
	if (points.length < 2) return false;
	if (points.some((pt) => pointInPolygon(pt, polygon))) return true;
	for (let i = 0; i < points.length - 1; i += 1) {
		if (segmentPolygonIntersection(points[i], points[i + 1], polygon)) return true;
	}
	return false;
};

/**
 * Clip a polyline at the polygon boundary (mode: "to" or "from").
 * @param {Array<{lat: number, lng: number}>|Array<[number, number]>} polyline
 * @param {Array<{lat: number, lng: number}>} zonePoints
 * @param {'to'|'from'} mode
 * @returns {Array<{lat: number, lng: number}>|Array<[number, number]>}
 */
export const clipPolylineAtZone = (polyline, zonePoints, mode) => {
	if (!Array.isArray(polyline) || polyline.length < 2) return polyline;
	if (!Array.isArray(zonePoints) || zonePoints.length < 3) return polyline;
	const points = polyline.map((pt) => normalizePolylinePoint(pt)).filter(Boolean);
	if (points.length < 2) return polyline;
	const isInside = (pt) => pointInPolygon(pt, zonePoints);
	const metersPerDegreeLat = 111320;
	const metersPerDegreeLng = (lat) =>
		metersPerDegreeLat * Math.cos((lat * Math.PI) / 180) || metersPerDegreeLat;
	const nudgeTowards = (point, target, meters = 1) => {
		if (!point || !target) return point;
		const lngFactor = metersPerDegreeLng(point.lat || 0);
		const dx = (target.lng - point.lng) * lngFactor;
		const dy = (target.lat - point.lat) * metersPerDegreeLat;
		const len = Math.sqrt(dx * dx + dy * dy);
		if (!len) return point;
		const scale = meters / len;
		return {
			lat: point.lat + (dy / metersPerDegreeLat) * scale,
			lng: point.lng + (dx / lngFactor) * scale,
		};
	};
	const nudgeOutside = (point, outside) => {
		if (!point || !outside) return point;
		let next = nudgeTowards(point, outside, 1);
		if (isInside(next)) {
			next = nudgeTowards(point, outside, 5);
		}
		return next;
	};
	const findFirstIntersection = () => {
		for (let i = 0; i < points.length - 1; i += 1) {
			const hit = segmentPolygonIntersection(points[i], points[i + 1], zonePoints);
			if (hit) return { index: i, point: hit };
		}
		return null;
	};

	if (mode === 'to') {
		const firstHit = findFirstIntersection();
		if (firstHit) {
			const outside = points[firstHit.index];
			const intersection = nudgeOutside(firstHit.point, outside);
			const clipped = points.slice(0, firstHit.index + 1).concat(intersection);
			return clipped.map((pt) => [pt.lat, pt.lng]);
		}
		if (!isInside(points[points.length - 1])) return polyline;
		let outsideIdx = -1;
		for (let i = points.length - 2; i >= 0; i -= 1) {
			if (!isInside(points[i])) {
				outsideIdx = i;
				break;
			}
		}
		if (outsideIdx === -1) return polyline;
		const outside = points[outsideIdx];
		const inside = points[outsideIdx + 1];
		const intersection = nudgeOutside(
			segmentPolygonIntersection(outside, inside, zonePoints) ||
				nearestPointOnPolygon(outside, zonePoints) ||
				outside,
			outside
		);
		if (!intersection) return polyline;
		const clipped = points.slice(0, outsideIdx + 1).concat(intersection);
		return clipped.map((pt) => [pt.lat, pt.lng]);
	}

	if (mode === 'from') {
		const firstHit = findFirstIntersection();
		if (firstHit) {
			const outside = points[firstHit.index + 1];
			const intersection = nudgeOutside(firstHit.point, outside);
			const clipped = [intersection].concat(points.slice(firstHit.index + 1));
			return clipped.map((pt) => [pt.lat, pt.lng]);
		}
		if (!isInside(points[0])) return polyline;
		let outsideIdx = -1;
		for (let i = 1; i < points.length; i += 1) {
			if (!isInside(points[i])) {
				outsideIdx = i;
				break;
			}
		}
		if (outsideIdx === -1) return polyline;
		const inside = points[outsideIdx - 1];
		const outside = points[outsideIdx];
		const intersection = nudgeOutside(
			segmentPolygonIntersection(inside, outside, zonePoints) ||
				nearestPointOnPolygon(outside, zonePoints) ||
				outside,
			outside
		);
		if (!intersection) return polyline;
		const clipped = [intersection].concat(points.slice(outsideIdx));
		return clipped.map((pt) => [pt.lat, pt.lng]);
	}

	return polyline;
};

/**
 * Sample polygon edges for road-snapping candidates.
 * @param {Array<{lat: number, lng: number}>} points
 * @param {number} maxSamples
 * @returns {Array<{lat: number, lng: number}>}
 */
export const samplePolygonEdges = (points, maxSamples = 48) => {
	if (!Array.isArray(points) || points.length < 2) return [];
	const samples = [];
	for (let i = 0; i < points.length; i += 1) {
		const a = points[i];
		const b = points[(i + 1) % points.length];
		if (!a || !b) continue;
		samples.push(a);
		samples.push({
			lat: a.lat + (b.lat - a.lat) / 3,
			lng: a.lng + (b.lng - a.lng) / 3,
		});
		samples.push({
			lat: a.lat + ((b.lat - a.lat) * 2) / 3,
			lng: a.lng + ((b.lng - a.lng) * 2) / 3,
		});
		samples.push({
			lat: (a.lat + b.lat) / 2,
			lng: (a.lng + b.lng) / 2,
		});
	}
	if (samples.length <= maxSamples) return samples;
	const step = Math.ceil(samples.length / maxSamples);
	return samples.filter((_, idx) => idx % step === 0);
};

/**
 * Pick the best road-snapped edge point with a preference bias.
 * @param {Array<object>} candidates
 * @param {{lat: number, lng: number}|null} preferredEdge
 * @param {{lat: number, lng: number}|null} reference
 * @returns {{lat: number, lng: number}|null}
 */
export const pickBestRoadEdge = (candidates, preferredEdge, reference) => {
	if (!Array.isArray(candidates) || candidates.length === 0) return null;
	let best = null;
	candidates.forEach((cand) => {
		const edgeDist = Number.isFinite(cand.edgeDistanceMeters)
			? cand.edgeDistanceMeters
			: Number.POSITIVE_INFINITY;
		const preferredDist = preferredEdge
			? distanceMeters(preferredEdge, cand.input)
			: Number.POSITIVE_INFINITY;
		const refInputDist = reference
			? distanceMeters(reference, cand.input)
			: Number.POSITIVE_INFINITY;
		const refRoadDist = reference
			? distanceMeters(reference, cand.point)
			: Number.POSITIVE_INFINITY;
		const score =
			refRoadDist * 4 + refInputDist * 1 + preferredDist * 0.5 + edgeDist * 0.5;
		if (!best || score < best.score) {
			best = { ...cand, score };
		}
	});
	return best?.point || null;
};

/**
 * Prefer a road point inside the zone if one exists; otherwise fallback to edge scoring.
 * @param {Array<object>} candidates
 * @param {{lat: number, lng: number}|null} preferredEdge
 * @param {{lat: number, lng: number}|null} reference
 * @param {Array<{lat: number, lng: number}>} polygon
 * @returns {{lat: number, lng: number}|null}
 */
export const pickBestRoadPoint = (candidates, preferredEdge, reference, polygon) => {
	if (!Array.isArray(candidates) || candidates.length === 0) return null;
	const insideCandidates =
		Array.isArray(polygon) && polygon.length >= 3
			? candidates.filter((cand) => isPointInsideOrOnZone(cand.point, polygon))
			: [];
	if (insideCandidates.length && reference) {
		let best = null;
		let bestDist = Number.POSITIVE_INFINITY;
		insideCandidates.forEach((cand) => {
			const refDist = distanceMeters(reference, cand.point);
			if (refDist < bestDist) {
				bestDist = refDist;
				best = cand;
			}
		});
		return best?.point || null;
	}
	const pool = insideCandidates.length ? insideCandidates : candidates;
	return pickBestRoadEdge(pool, preferredEdge, reference);
};


